import { Card, Flex, LoadingOverlay, useComputedColorScheme } from "@mantine/core";
import {
  Dialog,
  PrintableDropdownOptions,
  usePrintableContext,
  usePrintableSectionContext,
} from "@components";
import { calculateAgeForTest, parseDateOnly } from "@utils/CommonUtil";
import { buildTestResultFilename, replaceFilenameExtension } from "@utils/printableUtils";
import {
  changeTestPatient,
  deleteTest,
  updateTestClinicianReview,
} from "@utils/api-communication/RestApiCli";
import { ClinicianLabels, ClinicianReviewPayload, TestCommentEntry } from "types/tests";
import { rememberClinicianReviewAnswered } from "@utils/clinicianLabelUtils";
import { useCallback, useEffect, useMemo, useState } from "react";

import ChangePatientModal from "../../testContainer/result/ChangePatientModal";
import ConfirmChangePatientModal from "../../testContainer/result/ConfirmChangePatientModal";
import TestDetailsDialog from "../../testContainer/result/TestDetailsDialog";
import { Patient } from "types/patientTypes";
import { useDisclosure } from "@mantine/hooks";

import ClinicianReviewBanner from "../clinicianLabels/ClinicianReviewBanner";
import ClinicianReviewDialog from "../clinicianLabels/ClinicianReviewDialog";
import OrganizationDetailView from "./OrganizationDetailView";
import TestEntryHeader from "./TestEntryHeader";
import TestSelectorModal from "./TestSelectorModal";
import { selectPdfFilenameFormat } from "@store/userPreferences/userPreferencesSlice";
import { selectShowOrgDetailInResults } from "@store/ui/uiSelectors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePrintable } from "@hooks";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

interface TestResultSheetProps {
  entry: any;
  updateAge?: (age: number) => void;
  presetValues?: Record<string, number>;
  getResultSheetContent?: () => JSX.Element;
  canExportDicom?: boolean;
  onPatientChanged?: () => void;
}

export default function TestResultSheet({
  entry,
  updateAge,
  presetValues,
  getResultSheetContent,
  canExportDicom,
  onPatientChanged,
}: TestResultSheetProps) {
  const [patientAge, setPatientAge] = useState(25);
  const [showComment, setShowComment] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [comment, setComment] = useState<string>(entry.comment ?? "");
  const [commentHistory, setCommentHistory] = useState<TestCommentEntry[] | undefined>(
    entry.commentHistory,
  );
  const [clinicianLabels, setClinicianLabels] = useState<ClinicianLabels | undefined>(
    entry.clinicianLabels,
  );
  const [savingReview, setSavingReview] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [changingPatient, setChangingPatient] = useState(false);
  const [changePatientError, setChangePatientError] = useState<string | null>(null);
  const [changePatientOpened, { open: openChangePatient, close: closeChangePatient }] =
    useDisclosure(false);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [compareOpened, { open: openCompare, close: closeCompare }] = useDisclosure(false);

  const shouldShowOrgDetail = useSelector(selectShowOrgDetailInResults);
  const pdfFilenameFormat = useSelector(selectPdfFilenameFormat);

  const { t } = useTranslation();
  const { prettier } = usePrintableContext();
  const { floatingLogo } = usePrintableSectionContext();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";
  const pdfFilename = useMemo(
    () => buildTestResultFilename(entry, pdfFilenameFormat),
    [entry, pdfFilenameFormat],
  );
  const dicomFilename = useMemo(() => replaceFilenameExtension(pdfFilename, ".dcm"), [pdfFilename]);
  const { generate, loading } = usePrintable({ filename: pdfFilename });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const showReviewBanner = useMemo(() => {
    const first = (searchParams.get("uuid") ?? "").split(",")[0]?.trim();
    return first === entry.uuid;
  }, [searchParams, entry.uuid]);

  useEffect(() => {
    const pAge = calculateAgeForTest(new Date(entry.startDate), parseDateOnly(entry.patient.dob));
    setPatientAge(pAge);
    updateAge?.(pAge);
  }, []);

  const handleDelete = useCallback(() => {
    deleteTest(
      { uuid: entry.uuid },
      () => {
        navigate("/list");
        setShowDeleteConfirmation(false);
      },
      (err) => console.log("error on delete test", err),
      null,
    );
  }, [entry, navigate]);

  const handleSaveClinicianReview = useCallback(
    (payload: ClinicianReviewPayload) =>
      new Promise<void>((resolve, reject) => {
        setSavingReview(true);
        updateTestClinicianReview(
          entry.uuid,
          payload,
          (response) => {
            setSavingReview(false);
            rememberClinicianReviewAnswered(entry.uuid);
            const saved = response?.data;
            setClinicianLabels(saved?.clinicianLabels);
            setCommentHistory(saved?.commentHistory);
            if (payload.comment !== undefined) setComment(payload.comment);
            resolve();
          },
          (error) => {
            setSavingReview(false);
            reject(error);
          },
        );
      }),
    [entry.uuid],
  );

  const styles = useMemo(
    () => ({
      padding: prettier ? "md" : 0,
      shadow: prettier ? "md" : "none",
    }),
    [prettier],
  );

  function handleChangePatientSelect(newPatient: Patient) {
    setSelectedPatient(newPatient);
    closeChangePatient();
    openConfirm();
  }

  function handleConfirmChangePatient() {
    if (!entry?.uuid || !selectedPatient) return;
    setChangingPatient(true);
    setChangePatientError(null);
    changeTestPatient(
      entry.uuid,
      selectedPatient.uuid,
      () => {
        setChangingPatient(false);
        closeConfirm();
        setSelectedPatient(null);
        onPatientChanged?.();
      },
      () => {
        setChangingPatient(false);
        setChangePatientError(
          t("ResultList.changePatientError", "Failed to change patient. Please try again."),
        );
      },
    );
  }

  function handleCancelConfirm() {
    closeConfirm();
    setSelectedPatient(null);
    setChangePatientError(null);
  }

  const dropdownOptions = useMemo(
    () => [
      { children: t("ResultList.comment"), onClick: () => setShowComment(true) },
      { children: t("ResultList.testDetails"), onClick: () => setShowDetails(true) },
      { children: t("ResultList.changePatient"), onClick: openChangePatient },
      {
        children: t("ResultList.deleteTest"),
        onClick: () => setShowDeleteConfirmation(true),
      },
      canExportDicom && {
        children: t("ResultList.exportDicom"),
        onClick: () =>
          generate("dicom", {
            canExportDicom,
            filename: dicomFilename,
            testId: entry.uuid,
          }),
      },
      { children: t("ResultList.compareWithTest"), onClick: openCompare },
    ],
    [
      dicomFilename,
      entry.patient.uuid,
      entry.uuid,
      generate,
      canExportDicom,
      openChangePatient,
      openCompare,
      t,
    ],
  );

  return (
    <Card
      withBorder={prettier && !floatingLogo}
      shadow={prettier ? "none" : isDark ? "sm" : "none"}
      p={prettier ? 0 : "md"}
      radius="md"
      bg={prettier ? "transparent" : isDark ? "var(--oculera-surface-alt)" : undefined}
    >
      <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
      <Card
        withBorder={prettier}
        shadow={styles.shadow}
        pb={styles.padding}
        radius="md"
        mb={prettier ? "md" : 0}
      >
        <PrintableDropdownOptions
          filename={pdfFilename}
          showDownloadSettingsLink
          options={dropdownOptions}
        />
        <TestSelectorModal
          opened={compareOpened}
          onClose={closeCompare}
          patientId={entry.patient.uuid}
          uuid={entry.uuid}
        />
        <ChangePatientModal
          opened={changePatientOpened}
          onClose={closeChangePatient}
          onConfirm={handleChangePatientSelect}
        />
        <ConfirmChangePatientModal
          opened={confirmOpened}
          onClose={handleCancelConfirm}
          onConfirm={handleConfirmChangePatient}
          currentPatient={entry.patient}
          selectedPatient={selectedPatient}
          loading={changingPatient}
          error={changePatientError}
        />
        <TestEntryHeader entry={entry} patientAge={patientAge} presetValues={presetValues} />
      </Card>
      <Card
        withBorder={prettier}
        shadow={floatingLogo ? undefined : styles.shadow}
        mx={prettier ? 0 : "xl"}
        p="md"
        pt={styles.padding}
        pb={styles.padding}
        radius="md"
        pos="relative"
      >
        <h4 style={floatingLogo ? { position: "absolute", margin: 0, zIndex: 1 } : undefined}>
          {entry && entry["eye"] && t("ResultList." + entry["eye"])}
        </h4>
        {getResultSheetContent?.()}
        <Flex mt="sm" gap="md" align="flex-start" justify="flex-end" wrap="nowrap">
          {comment && comment.length > 0 && (
            <Card flex={1} withBorder radius="md" p="sm">
              <strong>{t("comments")}:</strong> {comment}
            </Card>
          )}

          {shouldShowOrgDetail && !(floatingLogo && prettier) && (
            <Flex justify="flex-end" align="center" flex="0 0 auto">
              <OrganizationDetailView organization={null} organizationId={entry.orgId} />
            </Flex>
          )}
        </Flex>
      </Card>
      <Dialog
        title={t("ResultList.deleteTestConfirmation")}
        open={showDeleteConfirmation}
        onCancel={setShowDeleteConfirmation}
        cancelLabel={t("ResultList.cancel")}
        onConfirm={handleDelete}
        confirmLabel={t("ResultList.deleteTest")}
      >
        <div>{t("ResultList.deleteTestConfirmationMessage")}</div>
      </Dialog>
      <ClinicianReviewDialog
        open={showComment}
        onClose={() => setShowComment(false)}
        comment={comment}
        labels={clinicianLabels}
        commentHistory={commentHistory}
        saving={savingReview}
        onSave={handleSaveClinicianReview}
      />
      <TestDetailsDialog
        open={showDetails}
        onClose={setShowDetails}
        device={entry.device}
        version={entry.version}
      />
      {showReviewBanner && !showComment && (
        <ClinicianReviewBanner
          testUuid={entry.uuid}
          labels={clinicianLabels}
          saving={savingReview}
          onSave={handleSaveClinicianReview}
        />
      )}
    </Card>
  );
}
